import { Injectable, inject, signal } from '@angular/core';
import { ElectronBridgeService } from './electron-bridge.service';
import { TestExecutorService } from './test-executor.service';
import { SessionPoolService } from './session-pool.service';
import { GapAnalysisResult, GapAnalysisResultType, GapAnalysisSummary } from '../models/gap-analysis.model';

export interface TestStep {
  type: 'login' | 'navigate' | 'click' | 'fill' | 'select' | 'wait' | 'verify';
  action?: string;
  selector?: string;
  value?: any;
  text?: string;
  timeout?: number;
  [key: string]: any;
}

export interface ScenarioConfig {
  scenarioId: string;
  scenarioName: string;
  steps: TestStep[];
  loginSteps: TestStep[];
  navDefaultSelections: { [navKey: string]: any };
  expectedElements: string[];
}

export interface NavDefaultMapping {
  navKey: string;
  selector: string;
  value: any;
}

export interface ExecutionCallbacks {
  onScenarioStart?: (index: number, scenarioId: string, scenarioName: string) => void;
  onStepComplete?: (index: number, scenarioId: string, stepResult: any) => void;
  onScenarioComplete?: (index: number, scenarioId: string, result: GapAnalysisResult) => void;
}

/**
 * GapAnalysisElectronService
 * Uses SessionPoolService + TestExecutorService — the exact same code path
 * as runner-bridge.service.ts parallel chain execution.
 * No custom electron logic. Same IPC, same timing, same screenshots.
 */
@Injectable({ providedIn: 'root' })
export class GapAnalysisElectronService {
  private electronBridge = inject(ElectronBridgeService);
  private testExecutor = inject(TestExecutorService);
  private sessionPool = inject(SessionPoolService);

  isRunning = signal(false);
  progress = signal(0);
  currentScenario = signal('');
  results = signal<GapAnalysisResult[]>([]);

  // When true, the per-step screenshot is dropped from the retained stepResults to avoid
  // OOM in long runs. Safe only when screenshots are persisted per-step elsewhere
  // (gap-execution). Left false for generation-execution which bulk-uploads them later.
  private stripStepScreenshots = false;

  /**
   * Run gap analysis with parallel execution.
   * Same flow as runner-bridge handleJobExecute:
   * 1. Initialize session pool
   * 2. For each scenario: acquireSession(FRESH) → executePreconditions → executeScenario
   * 3. Destroy all sessions
   */
  async runGapAnalysis(
    cloneId: string,
    tenantUrl: string,
    scenarios: ScenarioConfig[],
    navDefaults: NavDefaultMapping[],
    callbacks?: ExecutionCallbacks,
    maxParallel: number = 2,
    stripStepScreenshots: boolean = false
  ): Promise<GapAnalysisResult[]> {
    this.stripStepScreenshots = stripStepScreenshots;
    if (!this.electronBridge.isElectron) {
      console.warn('Gap analysis requires Electron environment');
      return [];
    }

    try {
      this.isRunning.set(true);
      this.progress.set(0);
      this.results.set([]);

      // Initialize session pool (same as runner-bridge:370)
      await this.sessionPool.initialize([], []);

      const analysisResults: GapAnalysisResult[] = new Array(scenarios.length);
      const totalScenarios = scenarios.length;
      let completed = 0;

      const MAX_PARALLEL = maxParallel;

      // Per-slot mutex (gap-analysis only — does NOT touch SessionPoolService which is shared
      // with test-execution and scheduled-execution). Two scenarios that hash to the same
      // gap-${index % MAX_PARALLEL} slot must serialize their acquire+execute+release so a
      // FRESH acquire never destroys a window the previous scenario's tail step is still using.
      const slotChain: Promise<void>[] = new Array(MAX_PARALLEL).fill(Promise.resolve());
      const runOnSlot = <T>(slotIdx: number, fn: () => Promise<T>): Promise<T> => {
        const prev = slotChain[slotIdx];
        const next = prev.then(fn, fn);
        slotChain[slotIdx] = next.then(() => undefined, () => undefined);
        return next;
      };

      // Slot rotates by scenario index modulo MAX_PARALLEL (Run-2 behavior, the only version
      // that completed 59/59 in cloud). workerId binding and 1500ms release delay regressed
      // gap-execution to crash at ~30 deterministically; reverting both.
      const executeOne = async (index: number, workerId: number): Promise<void> => {
        const scenario = scenarios[index];
        this.currentScenario.set(scenario.scenarioName);

        try {
          callbacks?.onScenarioStart?.(index, scenario.scenarioId, scenario.scenarioName);

          const sessionKey = {
            authProfileId: `gap-${index % MAX_PARALLEL}`,
            tenantId: cloneId
          };
          const session = await this.sessionPool.acquireSession(sessionKey, 'FRESH', tenantUrl);
          await new Promise(resolve => setTimeout(resolve, 3000));

          const result = await this.executeScenarioViaTestExecutor(scenario, session, callbacks ? (stepResult: any) => {
            callbacks.onStepComplete?.(index, scenario.scenarioId, stepResult);
          } : undefined);
          analysisResults[index] = result;
          await callbacks?.onScenarioComplete?.(index, scenario.scenarioId, result);
          // Free retained per-step screenshots/details after the API callback has shipped them.
          // Keeps long runs flat in memory; UI signals only need scenarioId/scenarioName/result/failedStep.
          this.releaseScenarioMemory(analysisResults[index]);
        } catch (error) {
          const errorResult: GapAnalysisResult = {
            scenarioId: scenario.scenarioId,
            scenarioName: scenario.scenarioName,
            result: 'cascade_skipped',
            stepDetail: `Error: ${String(error)}`,
            failedStep: 0
          };
          analysisResults[index] = errorResult;
          // CRITICAL: Always invoke onScenarioComplete callback on error, so caller can finalize DB rows.
          // Without this, if scenario throws, the result row stays 'pending' forever.
          await callbacks?.onScenarioComplete?.(index, scenario.scenarioId, errorResult);
          this.releaseScenarioMemory(analysisResults[index]);
        } finally {
          await this.sessionPool.releaseSession({ authProfileId: `gap-${index % MAX_PARALLEL}`, tenantId: cloneId });
          completed++;
          this.progress.set(Math.round((completed / totalScenarios) * 100));
          this.results.set(analysisResults.filter(Boolean));
        }
      };

      // Parallel execution with concurrency limit (same as runner-bridge:runWithConcurrency).
      // Each scenario is queued onto its slot's mutex chain so same-slot scenarios serialize.
      let nextIndex = 0;
      const runNext = async (workerId: number): Promise<void> => {
        while (nextIndex < scenarios.length) {
          const idx = nextIndex++;
          await runOnSlot(idx % MAX_PARALLEL, () => executeOne(idx, workerId));
        }
      };

      const workers = Array.from(
        { length: Math.min(MAX_PARALLEL, scenarios.length) },
        (_, workerId) => runNext(workerId)
      );
      await Promise.all(workers);

      // Destroy all sessions (same as runner-bridge:424)
      await this.sessionPool.destroyAll();

      this.isRunning.set(false);
      this.progress.set(100);
      this.results.set(analysisResults);

      return analysisResults;
    } catch (error) {
      console.error('Gap analysis error:', error);
      await this.sessionPool.destroyAll();
      this.isRunning.set(false);
      throw error;
    }
  }

  /**
   * Release per-step screenshot/detail data retained on a finalized result so 59-scenario
   * runs do not accumulate ~30 MB+ of base64 image data per worker. The summary fields the
   * UI actually reads (scenarioId, scenarioName, result, stepDetail, failedStep) are kept.
   */
  private releaseScenarioMemory(result: GapAnalysisResult | undefined): void {
    if (!result) return;
    const r = result as any;
    if (Array.isArray(r.stepResults)) {
      for (const sr of r.stepResults) {
        if (sr && typeof sr === 'object') {
          if ('screenshot' in sr) sr.screenshot = undefined;
          if ('screenshotData' in sr) sr.screenshotData = undefined;
          if ('result' in sr && sr.result && typeof sr.result === 'object') {
            if ('screenshot' in sr.result) sr.result.screenshot = undefined;
          }
        }
      }
    }
    if ('screenshot' in r) r.screenshot = undefined;
    if ('screenshotData' in r) r.screenshotData = undefined;
  }

  /**
   * Execute scenario by calling executeStep directly for each step.
   * Bypasses executeScenario/parseMultiStepScenario to send step objects
   * as-is to IPC without any text field reconstruction or smart matching.
   */
  private async executeScenarioViaTestExecutor(scenario: ScenarioConfig, session: any, onStepComplete?: (stepResult: any) => void): Promise<GapAnalysisResult> {
    try {
      const allSteps = scenario.steps;
      if (!allSteps || allSteps.length === 0) {
        return {
          scenarioId: scenario.scenarioId,
          scenarioName: scenario.scenarioName,
          result: 'pass',
          stepDetail: 'No steps to execute',
          failedStep: 0
        };
      }

      const stepResults: any[] = [];
      let failed = false;
      let failedIdx = -1;

      for (let i = 0; i < allSteps.length; i++) {
        if (failed) {
          stepResults.push({
            stepIndex: i + 1,
            action: allSteps[i]?.action || 'unknown',
            selector: typeof allSteps[i]?.selector === 'object' ? JSON.stringify(allSteps[i].selector) : '',
            value: allSteps[i]?.value || '',
            description: (allSteps[i] as any)?.description || '',
            result: 'skipped' as const,
            scenarioId: scenario.scenarioId,
          });
          continue;
        }

        // Inter-step delay (matches TestExecutorService line 981)
        if (i > 0) {
          const prevAction = allSteps[i - 1]?.action;
          const delayMs = prevAction === 'navigate' ? 6000 :
                         prevAction === 'click' ? 5000 : 4000;
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }

        // Build clean IPC step — only fields the DB version has.
        // Exclude 'description'/'id'/'type' which leak into text via IPC pipeline.
        const raw = allSteps[i] as any;
        const step = {
          action: raw.action,
          selector: raw.selector || {},
          value: raw.value ?? '',
          text: raw.action === 'pressEnter' ? '' : (raw.text ?? ''),
          timeout: Math.max(raw.timeout || 10000, 30000),
          description: (raw as any).description || '',
        } as any;
        try {
          const result = await this.testExecutor.executeStep(step, false, session);

          const success = result?.success !== false;
          const isVerify = step.action === 'verify' || step.action === 'assert' || step.action?.startsWith('verifyTable');
          const assertion = result?.result?.assertion || result?.assertion;
          const verifyPassed = assertion?.passed !== false;

          let stepResult: 'pass' | 'fail' | 'mismatch';
          if (success || (isVerify && !verifyPassed)) {
            stepResult = isVerify && !verifyPassed ? 'mismatch' : 'pass';
          } else {
            stepResult = 'fail';
          }

          const stepResultObj = {
            stepIndex: i + 1,
            action: step.action || 'unknown',
            selector: typeof step.selector === 'object' ? JSON.stringify(step.selector) : '',
            value: step.value || '',
            description: (raw as any).description || step.description || '',
            result: stepResult,
            errorMessage: result?.error || undefined,
            expected: assertion?.expected || undefined,
            actual: assertion?.actual || undefined,
            screenshotData: result?.screenshot || result?.result?.screenshot || undefined,
            scenarioId: scenario.scenarioId,
            durationMs: result?.duration || undefined,
          };
          // Hand the FULL object (with screenshot) to onStepComplete first — in gap-execution it
          // POSTs the step (incl. screenshot) to the API and owns that buffer from here on.
          onStepComplete?.(stepResultObj);

          // Retain in the results array. When stripStepScreenshots is set (gap-execution, where
          // screenshots are already persisted per-step), store a SCREENSHOT-FREE copy so we don't
          // hold 59 * ~10 * 542KB ≈ 320MB of base64 in the single runner renderer heap (OOM ~scenario 29).
          // generation-execution leaves it false: it has no per-step save and bulk-uploads stepResults
          // (with screenshots) AFTER runGapAnalysis returns, so the buffers must be kept there.
          // Timing-safe either way: we never mutate the object already handed to the async HTTP save.
          stepResults.push(
            this.stripStepScreenshots ? { ...stepResultObj, screenshotData: undefined } : stepResultObj
          );

          // Release the screenshot held on the raw executor result too.
          if (result) {
            result.screenshot = undefined;
          }
          if (result?.result) {
            result.result.screenshot = undefined;
          }

          if (!success && !isVerify) {
            failed = true;
            failedIdx = i;
          }
        } catch (stepError: any) {
          const failStepResult = {
            stepIndex: i + 1,
            action: step.action || 'unknown',
            selector: typeof step.selector === 'object' ? JSON.stringify(step.selector) : '',
            value: step.value || '',
            description: (raw as any).description || step.description || '',
            result: 'fail' as const,
            errorMessage: stepError?.message || String(stepError),
            scenarioId: scenario.scenarioId,
          };
          stepResults.push(failStepResult);
          onStepComplete?.(failStepResult);
          failed = true;
          failedIdx = i;
        }
      }

      if (failed) {
        return {
          scenarioId: scenario.scenarioId,
          scenarioName: scenario.scenarioName,
          result: 'cascade_skipped',
          stepDetail: `Step ${failedIdx + 1} failed: ${stepResults[failedIdx]?.errorMessage || 'unknown'}`,
          failedStep: failedIdx + 1,
          stepResults,
        };
      }

      return {
        scenarioId: scenario.scenarioId,
        scenarioName: scenario.scenarioName,
        result: 'pass',
        stepDetail: `All ${allSteps.length} steps executed successfully`,
        failedStep: allSteps.length,
        stepResults,
      };
    } catch (error) {
      return {
        scenarioId: scenario.scenarioId,
        scenarioName: scenario.scenarioName,
        result: 'cascade_skipped',
        stepDetail: `Error: ${String(error)}`,
        failedStep: 0
      };
    }
  }


  calculateSummary(
    results: GapAnalysisResult[],
    clientName: string,
    packageName: string,
    skeletonName: string
  ): GapAnalysisSummary {
    return {
      passed: results.filter(r => r.result === 'pass').length,
      elementFailures: results.filter(r => r.result === 'element_fail').length,
      cascadeSkipped: results.filter(r => r.result === 'cascade_skipped').length,
      navDefaultFailed: results.filter(r => r.result === 'nav_default_failed').length,
      total: results.length,
      runTimestamp: new Date().toISOString(),
      clientName,
      packageName,
      skeletonName
    };
  }
}
